import "dayjs/locale/sv";
import _ from "lodash";
import { observer } from "mobx-react";
import React from "react";
import { FormattedMessage } from "react-intl";
import { Spacer } from "~/components/Spacer";
import { BodyText } from "~/components/V2/Text/BodyText";
import { BrandText } from "~/components/V2/Text/BrandText";
import { EmptyTimeslot, TimeslotChip } from "~/components/V2/TimeslotChip";
import { View } from "~/components/V2/View";
import type { TimeslotInstance } from "~/mobx/entities/timeslots/Timeslot";
import { styleConstantsV2 } from "~/style/styleConstantsV2";

export const TimeslotList = observer(function TimeslotList({
  data,
  title,
  clinicTimezone,
}: {
  data: TimeslotInstance[];
  title: React.ReactNode;
  clinicTimezone: string;
}) {
  // fill the last row with empty slots
  const rows = _.chunk(data, 4);
  const lastRow = rows[rows.length - 1];
  const emptySlots = 4 - (lastRow?.length ?? 0);

  return (
    <>
      <BrandText sizeH4 weightBold colorBlack>
        {title}
      </BrandText>
      <Spacer small />

      {data.length === 0 ? (
        <View>
          <BodyText sizeLarge weightRegular colorGrey>
            <FormattedMessage
              id="uH7eR5"
              defaultMessage="No available times, choose another date"
              description="No available timeslots message"
            />
          </BodyText>
        </View>
      ) : (
        <View
          style={{
            gap: styleConstantsV2.spacing16,
          }}
        >
          {rows.map((row, index) => {
            const isLastRow = index === rows.length - 1;

            return (
              <View
                key={index}
                flex
                flexDirectionRow
                style={{ gap: styleConstantsV2.spacing16 }}
              >
                {row.map((timeslot) => {
                  return (
                    <TimeslotChip
                      timeslot={timeslot}
                      key={timeslot.startUtc}
                      clinicTimezone={clinicTimezone}
                    />
                  );
                })}
                {/* Fill the row with empty timeslots if needed */}
                {isLastRow &&
                  emptySlots > 0 &&
                  Array.from({ length: emptySlots }).map((_, i) => {
                    return <EmptyTimeslot key={i} />;
                  })}
              </View>
            );
          })}
        </View>
      )}
    </>
  );
});
